/**
 * CUSTOMER_ORDER — the multilingual QR surface.
 *
 * Reached at `/c/:token`, never by logging in. No staff token, no app chrome,
 * no Vietnamese-only assumptions.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE TWO GUARANTEES LIVE
 * ---------------------------------------------------------------------------
 * (1) SWITCHING LANGUAGE MUST NOT CLEAR THE CART.
 *     The cart is `useState` HERE, in the component that owns the session —
 *     which sits ABOVE `CustomerI18nProvider` in the tree. Changing the locale
 *     re-renders the provider and refetches the string bundle and the menu; it
 *     cannot touch state that is not its own. The cart survives by construction
 *     rather than by a `useEffect` remembering to preserve it.
 *
 * (2) SWITCHING LANGUAGE MUST NOT CHANGE THE CURRENCY.
 *     `currency` is read ONCE from `GET /customer/session` (the branch's
 *     `currency` / `currency_exponent`) and passed down as a prop. See
 *     CURRENCY GUARANTEE #1 and #2 in `CustomerI18nProvider.tsx`: the locale
 *     response's echoed `currency` is deliberately discarded.
 *
 * ---------------------------------------------------------------------------
 * LAYOUT RULES FOR LONG STRINGS
 * ---------------------------------------------------------------------------
 * German and Russian labels run 60–100% longer than the Vietnamese source
 * ("Xác nhận" → "Bestätigen" → "Подтвердить"). NO text container in this
 * subtree has a fixed height; everything wearing `.text-flow` wraps and grows
 * (see index.css). Buttons use min-height, never height.
 */

import { useEffect, useMemo, useState } from 'react';
import { LoadingState, ErrorState } from '@sonauto/ui';
import { createCustomerApi, createCustomerClient } from '../lib/api';
import { useAsync, usePoll } from '../lib/hooks';
import { CustomerI18nProvider, useCustomerI18n } from './CustomerI18nProvider';
import { LanguageSwitcher } from './LanguageSwitcher';
import { MenuScreen } from './MenuScreen';
import { CartScreen } from './CartScreen';
import { OrdersScreen } from './OrdersScreen';
import { HelpScreen } from './HelpScreen';
import { RatingScreen } from './RatingScreen';
import type { GuestCart } from './cart';
import { guestCartCount } from './cart';
import type { CustomerMenuResponse, CustomerOrdersResponse, CustomerSessionResponse } from '../lib/types';

export type CustomerView = 'MENU' | 'CART' | 'ORDERS' | 'HELP' | 'RATE';

export function CustomerApp({ token }: { readonly token: string }): React.JSX.Element {
  // One client, one api, for the life of the mount. Recreating it per render
  // would recreate the timeout/abort plumbing on every keystroke.
  const client = useMemo(() => createCustomerClient(), []);
  const api = useMemo(() => createCustomerApi(client, token), [client, token]);

  /** THE CART. Owned here — above the i18n provider. See guarantee (1). */
  const [cart, setCart] = useState<GuestCart>([]);
  const [view, setView] = useState<CustomerView>('MENU');

  const session = useAsync<CustomerSessionResponse>((signal) => api.session({ signal }), [api]);

  // A guest reading an allergen list must be able to pinch-zoom. index.html
  // locks scaling for the kiosk shells; the guest surface unlocks it.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const previous = meta?.getAttribute('content') ?? null;
    meta?.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
    return () => {
      if (meta !== null && previous !== null) meta.setAttribute('content', previous);
    };
  }, []);

  if (session.initialLoading) return <LoadingState label="Đang mở bàn của bạn…" />;

  if (session.error !== null || session.data === null) {
    return (
      <div className="font-customer p-6">
        <ErrorState
          title="Không mở được phiên gọi món"
          message={
            session.error?.message ??
            'Mã QR có thể đã hết hạn hoặc bàn đã được đóng. Vui lòng gọi nhân viên để được hỗ trợ.'
          }
          onRetry={session.reload}
        />
      </div>
    );
  }

  const data = session.data;

  if (data.device.status === 'BLOCKED') {
    return (
      <div className="font-customer p-6">
        <ErrorState
          title="Thiết bị bị chặn"
          message="Vui lòng gọi nhân viên để được hỗ trợ đặt món."
        />
      </div>
    );
  }

  return (
    <CustomerI18nProvider
      api={api}
      initialLocale={data.locale}
      locales={data.locales}
      currency={data.currency}
      onLocaleChanged={() => session.reload()}
    >
      <CustomerFrame
        session={data}
        view={view}
        setView={setView}
        cart={cart}
        setCart={setCart}
        api={api}
        pendingApproval={data.device.status === 'PENDING_APPROVAL'}
      />
    </CustomerI18nProvider>
  );
}

interface FrameProps {
  readonly session: CustomerSessionResponse;
  readonly view: CustomerView;
  readonly setView: (view: CustomerView) => void;
  readonly cart: GuestCart;
  readonly setCart: (cart: GuestCart) => void;
  readonly api: ReturnType<typeof createCustomerApi>;
  readonly pendingApproval: boolean;
}

function CustomerFrame({ session, view, setView, cart, setCart, api, pendingApproval }: FrameProps): React.JSX.Element {
  const { locale, tOr } = useCustomerI18n();

  // The menu is refetched when the locale changes: dish names, descriptions and
  // allergen wording are all translated server-side.
  const menu = useAsync<CustomerMenuResponse>((signal) => api.menu({ signal }), [api, locale], {
    enabled: !pendingApproval,
  });

  // Order status polling. 10s is a compromise: fast enough that "đang chuẩn bị
  // → sẵn sàng" feels live, slow enough that fifty phones on one table's wifi
  // do not saturate the branch's uplink.
  const orders = usePoll<CustomerOrdersResponse>((signal) => api.orders({ signal }), 10_000, [api], {
    enabled: !pendingApproval,
  });

  const count = guestCartCount(cart);

  return (
    <div className="font-customer flex min-h-full flex-col bg-canvas text-fg">
      <header className="sticky top-0 z-30 flex items-start gap-3 border-b border-line bg-surface px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="text-flow text-base font-bold leading-tight">{session.restaurant.name}</p>
          <p className="text-flow text-sm text-fg-muted">
            {session.restaurant.branchName}
            {session.table.displayName === null ? '' : ` · ${session.table.displayName}`}
          </p>
        </div>
        {/* Persistent, on every screen. */}
        <LanguageSwitcher />
      </header>

      {pendingApproval ? (
        <div className="text-flow m-4 rounded-lg bg-warning-bg p-4 text-warning-fg">
          {tOr(
            'customer.session.pending_approval',
            'Nhân viên đang xác nhận thiết bị của bạn. Vui lòng đợi trong giây lát.',
          )}
        </div>
      ) : null}

      <main className="min-h-0 flex-1 pb-24">
        {/* While the device waits for staff approval the API refuses /menu,
            /orders and /requests (403 DEVICE_PENDING_APPROVAL), so nothing
            below is rendered — a spinner that can never resolve is worse than
            the banner above, which says what is actually happening. */}
        {!pendingApproval && view === 'MENU' ? (
          <MenuScreen
            menu={menu.data}
            loading={menu.initialLoading}
            error={menu.error}
            onRetry={menu.reload}
            cart={cart}
            setCart={setCart}
            maxItemsPerOrder={session.session.maxItemsPerOrder}
          />
        ) : null}

        {!pendingApproval && view === 'CART' ? (
          <CartScreen
            api={api}
            cart={cart}
            setCart={setCart}
            requiresStaffConfirmation={session.session.requiresStaffConfirmation}
            onSubmitted={() => setView('ORDERS')}
          />
        ) : null}

        {!pendingApproval && view === 'ORDERS' ? (
          <OrdersScreen
            orders={orders.data}
            loading={orders.initialLoading}
            error={orders.error}
            onRetry={orders.reload}
            onOrderMore={() => setView('MENU')}
            onRate={() => setView('RATE')}
          />
        ) : null}

        {!pendingApproval && view === 'HELP' ? <HelpScreen api={api} /> : null}

        {!pendingApproval && view === 'RATE' ? <RatingScreen api={api} onDone={() => setView('ORDERS')} /> : null}
      </main>

      {/* Bottom nav: min-height only, labels wrap. A German label must not clip. */}
      <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-line bg-surface">
        <NavButton active={view === 'MENU'} onClick={() => setView('MENU')} icon="🍽️" label={tOr('customer.nav.menu', 'Thực đơn')} />
        <NavButton
          active={view === 'CART'}
          onClick={() => setView('CART')}
          icon="🛒"
          label={tOr('customer.nav.cart', 'Giỏ hàng')}
          badge={count > 0 ? count : undefined}
        />
        <NavButton active={view === 'ORDERS'} onClick={() => setView('ORDERS')} icon="🧾" label={tOr('customer.nav.orders', 'Đơn của tôi')} />
        <NavButton active={view === 'HELP'} onClick={() => setView('HELP')} icon="🔔" label={tOr('customer.nav.help', 'Gọi nhân viên')} />
      </nav>
    </div>
  );
}

function NavButton({
  active,
  onClick,
  icon,
  label,
  badge,
}: {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly icon: string;
  readonly label: string;
  readonly badge?: number;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      className={`relative flex min-h-[64px] flex-col items-center justify-center gap-0.5 px-1 py-2 ${
        active ? 'text-brand-700' : 'text-fg-muted'
      }`}
    >
      <span aria-hidden="true" className="text-xl leading-none">
        {icon}
      </span>
      {/* text-flow: no fixed height, wraps for long translations. */}
      <span className="text-flow text-center text-[11px] font-medium leading-tight">{label}</span>
      {badge !== undefined ? (
        <span className="absolute right-3 top-1 min-w-[20px] rounded-full bg-danger-solid px-1.5 text-[11px] font-bold text-white">
          {badge}
        </span>
      ) : null}
    </button>
  );
}
